/**
 * One customer: where they came from, what the partner earned from them and
 * what happened when (`GET /api/partner/customers/:id`). The name stays
 * masked; a 404 means the id is not one of this partner's customers.
 */
import { PageHeader } from "@nt/ui/page-header";
import { buttonVariants } from "@nt/ui/recipes";
import { EmptyState } from "@nt/ui/states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableStack,
  TableStackField,
  TableStackFields,
  TableStackRow,
  TableStackTitle,
} from "@nt/ui/table";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarClock,
  CalendarDays,
  CircleDollarSign,
  UserX,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router";

import { QueryView, type QueryLike } from "@/components/query-view";
import { Section } from "@/components/section";
import { StatTile } from "@/components/stat-tile";
import { StatusChip } from "@/components/status-chip";
import { apiGet, ApiError } from "@/lib/api";
import { money, shortDate, countryName } from "@/lib/format";
import type { CustomerDetail } from "@/lib/types";

import { edgeToEdge, sourceLabel } from "./shared";

type Earning = CustomerDetail["earnings"][number];

const back = <Link to="/customers">← Customers</Link>;

/** "Paid 1 Nov 2026", "Held until 9 Nov 2026", or nothing. */
function earningWhen(e: Pick<Earning, "paidAt" | "holdUntil">): string {
  if (e.paidAt) return `Paid ${shortDate(e.paidAt)}`;
  if (e.holdUntil) return `Held until ${shortDate(e.holdUntil)}`;
  return "";
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-compact text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm text-foreground">{children}</dd>
    </div>
  );
}

/** Earning statuses that pay nothing, so the customer's one fee is still open. */
const NOT_EARNED = new Set(["reversed", "rejected", "not_eligible"]);

const windowIsOpen = (data: CustomerDetail): boolean =>
  new Date(data.windowEndsAt).getTime() > Date.now();

const windowEnded = (data: CustomerDetail): string =>
  `The window ended on ${shortDate(data.windowEndsAt)}; a later purchase does not earn a fee.`;

/** The program pays one fee per customer, from the first paid order. */
function windowHint(data: CustomerDetail): string {
  const earned = data.earnings.some(
    (e) => e.kind === "first_purchase" && !NOT_EARNED.has(e.status),
  );
  if (earned) return "You earned this customer's one fee.";
  return windowIsOpen(data)
    ? "The first paid order until then earns you a fee."
    : windowEnded(data);
}

function Earnings({ data }: { data: CustomerDetail }) {
  if (data.earnings.length === 0) {
    return (
      <EmptyState
        title="No fees yet"
        description={
          windowIsOpen(data)
            ? `You earn a fee when this customer buys before ${shortDate(data.windowEndsAt)}.`
            : windowEnded(data)
        }
      />
    );
  }
  return (
    <>
      <div className={`hidden sm:block ${edgeToEdge}`}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Product</TableHead>
              <TableHead className="text-right">Fee</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Hold or payment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.earnings.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="whitespace-nowrap">
                  {shortDate(e.createdAt)}
                </TableCell>
                <TableCell className="normal-nums">
                  {e.product ?? "Order"}
                </TableCell>
                <TableCell className="text-right">
                  {money(e.feeCents)}
                </TableCell>
                <TableCell>
                  <StatusChip status={e.status} />
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {earningWhen(e)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TableStack className="sm:hidden">
        {data.earnings.map((e) => (
          <TableStackRow key={e.id} className="px-(--card-spacing)">
            <TableStackTitle>
              <span className="min-w-0 truncate">{e.product ?? "Order"}</span>
              <StatusChip status={e.status} />
            </TableStackTitle>
            <TableStackFields>
              <TableStackField label="Date">
                {shortDate(e.createdAt)}
              </TableStackField>
              <TableStackField label="Fee">{money(e.feeCents)}</TableStackField>
              {earningWhen(e) ? (
                <TableStackField label="Hold or payment">
                  {earningWhen(e)}
                </TableStackField>
              ) : null}
            </TableStackFields>
          </TableStackRow>
        ))}
      </TableStack>
    </>
  );
}

const DOT: Record<CustomerDetail["timeline"][number]["kind"], string> = {
  click: "bg-muted-foreground",
  signup: "bg-info",
  fee: "bg-primary",
  payable: "bg-success",
  paid: "bg-success",
  reversed: "bg-danger",
  rejected: "bg-danger",
};

function Activity({ items }: { items: CustomerDetail["timeline"] }) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No activity yet. Clicks, sign-ups and fees show up here as they happen.
      </p>
    );
  }
  return (
    <ol className="relative flex flex-col gap-5 before:absolute before:top-2 before:bottom-2 before:left-[5px] before:w-px before:bg-border">
      {items.map((item, i) => (
        <li key={`${item.at}-${i}`} className="relative flex gap-3 pl-6">
          <span
            aria-hidden
            className={`absolute top-1.5 left-0 size-[11px] rounded-full ring-4 ring-card ${DOT[item.kind] ?? "bg-muted-foreground"}`}
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <time
              dateTime={item.at}
              className="text-compact text-muted-foreground"
            >
              {shortDate(item.at)}
            </time>
            <p className="text-sm text-foreground">{item.text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function CustomerDetailView({ data }: { data: CustomerDetail }) {
  return (
    <>
      <PageHeader
        back={back}
        title={
          <span className="flex flex-wrap items-center gap-3">
            {data.name}
            <StatusChip status={data.status} />
          </span>
        }
        description="Where this customer came from and what you earned. Names are masked to protect customers."
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile
          label="Signed up"
          value={shortDate(data.signedUpAt)}
          icon={CalendarDays}
        />
        <StatTile
          label="Total fees"
          value={money(data.totalFeesCents)}
          icon={CircleDollarSign}
        />
        <StatTile
          label="Attribution window ends"
          value={shortDate(data.windowEndsAt)}
          hint={windowHint(data)}
          icon={CalendarClock}
        />
      </div>
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <Section title="Details">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Fact label="Country">
                {data.country ? countryName(data.country) : "Unknown"}
              </Fact>
              <Fact label="Source">{sourceLabel(data)}</Fact>
              <Fact label="Landing page">
                {data.landingPath ?? "Not recorded"}
              </Fact>
              <Fact label="Referrer">
                {data.referrerHost ?? "Direct or unknown"}
              </Fact>
              <Fact label="Clicked at">
                {data.clickedAt
                  ? shortDate(data.clickedAt)
                  : "No click recorded"}
              </Fact>
            </dl>
          </Section>
          <Section
            title="Earnings"
            description="Every fee this customer earned you."
            contentClassName={data.earnings.length ? "px-0" : undefined}
          >
            <Earnings data={data} />
          </Section>
        </div>
        <Section title="Activity" description="Newest first.">
          <Activity items={data.timeline} />
        </Section>
      </div>
    </>
  );
}

function NotFound({ message }: { message: string }) {
  return (
    <>
      <PageHeader back={back} title="Customer" />
      <EmptyState
        icon={<UserX />}
        title="Customer not found"
        description={message}
        action={
          <Link to="/customers" className={buttonVariants()}>
            Back to Customers
          </Link>
        }
      />
    </>
  );
}

export function CustomerDetailScreen({
  query,
}: {
  query: QueryLike<CustomerDetail>;
}) {
  if (
    query.isError &&
    query.error instanceof ApiError &&
    query.error.status === 404
  ) {
    return <NotFound message={query.error.message} />;
  }
  if (query.data !== undefined && !query.isError) {
    return <CustomerDetailView data={query.data} />;
  }
  return (
    <>
      <PageHeader back={back} title="Customer" />
      <QueryView query={query} what="this customer" rows={4}>
        {() => null}
      </QueryView>
    </>
  );
}

/** A 404 is final: no retries before the not-found state. */
const retry = (count: number, error: unknown) =>
  !(error instanceof ApiError && error.status === 404) && count < 3;

export function CustomerDetailPage() {
  const { id = "" } = useParams();
  const query = useQuery({
    queryKey: ["customer", id],
    queryFn: () =>
      apiGet<CustomerDetail>(
        `/api/partner/customers/${encodeURIComponent(id)}`,
      ),
    retry,
  });
  return <CustomerDetailScreen query={query} />;
}
